import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Compass,
  ArrowRight,
  CheckCircle2,
  Target,
  BarChart3,
  Lightbulb,
  RefreshCw,
  Layers,
  TrendingUp,
} from "lucide-react";

const features = [
  {
    icon: Target,
    title: "3-Tier Probabilistic Matching",
    description: "Hard filters eliminate ineligible grants, soft scoring ranks by 22 weighted features, semantic similarity finds mission-aligned opportunities.",
  },
  {
    icon: BarChart3,
    title: "8-Measures Dashboard",
    description: "See Opportunities ($), Expectations, Survey, Outlook, Leverage, Positioning, Efficiency, and Assurance for every matched grant.",
  },
  {
    icon: Lightbulb,
    title: "Near-Miss Alerts",
    description: "Know exactly why you didn't qualify and get AI-powered repositioning suggestions to close the gap.",
  },
  {
    icon: RefreshCw,
    title: "Always Fresh Data",
    description: "Weekly GrantConnect sync and automated crawling keeps all 600+ grant programs current.",
  },
];

const measures = [
  { name: "Opportunities", description: "Total qualified funding available", example: "$250,000" },
  { name: "Expectations", description: "Probability-weighted expected value", example: "$172,500" },
  { name: "Survey", description: "$/entity (competition density)", example: "$45,000" },
  { name: "Outlook", description: "YoY funding trend", example: "+12.5%" },
  { name: "Leverage", description: "L1-L4 goal alignment score", example: "0.87" },
  { name: "Positioning", description: "Rank vs historical recipients", example: "82%ile" },
  { name: "Efficiency", description: "Expected $ per effort hour", example: "$8,625" },
  { name: "Assurance", description: "Funder reliability score", example: "$155,250" },
];

export const FundMatchPage = () => {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 gradient-hero" data-testid="fundmatch-hero">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Badge className="mb-6 bg-gradient-to-r from-slate-100 to-teal-100 text-teal-700 hover:from-slate-100 hover:to-teal-100 border border-teal-200/50 px-4 py-1.5 text-sm font-medium">
                  <Compass className="h-3.5 w-3.5 mr-1.5 text-teal-500" />
                  Grant Discovery
                </Badge>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-zinc-900 tracking-tight mb-6"
              >
                FundMatch
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-lg md:text-xl text-zinc-600 leading-relaxed mb-8"
              >
                The most accurate AI-powered grant discovery engine for Canadian nonprofits. 
                Surface every funding opportunity ranked by probability of success, opportunity 
                size, and strategic fit.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="flex flex-col sm:flex-row gap-4"
              >
                <Link to="/dashboard">
                  <Button
                    size="lg"
                    className="bg-gradient-to-r from-slate-500 to-teal-500 hover:from-slate-600 hover:to-teal-600 text-white font-medium px-8"
                    data-testid="fundmatch-cta"
                  >
                    Start Discovering
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="relative"
            >
              <div className="bg-white rounded-2xl shadow-2xl border border-zinc-100 p-6 md:p-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-heading font-semibold text-zinc-900">Top Matches</h3>
                  <Badge className="bg-emerald-100 text-emerald-700">Live</Badge>
                </div>
                <div className="space-y-4">
                  {[
                    { name: "Community Resilience Fund", score: 92, amount: "$250K" },
                    { name: "Youth Mental Health Initiative", score: 88, amount: "$175K" },
                    { name: "Indigenous Community Support", score: 85, amount: "$500K" },
                  ].map((grant, i) => (
                    <div key={i} className="flex items-center justify-between p-4 bg-zinc-50 rounded-xl">
                      <div>
                        <p className="font-medium text-zinc-900 text-sm">{grant.name}</p>
                        <p className="text-xs text-zinc-500">{grant.amount}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right">
                          <p className="text-sm font-bold text-emerald-600">{grant.score}%</p>
                          <p className="text-xs text-zinc-500">match</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 8 Measures Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="measures-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-amber-100 text-amber-700 hover:bg-amber-100 border-0">
              Comprehensive Analysis
            </Badge>
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-zinc-900 tracking-tight mb-4">
              8-Measures Dashboard
            </h2>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
              Every grant opportunity is analyzed across 8 dimensions with confidence intervals, 
              giving you a complete picture beyond just a ranked list.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {measures.map((measure, index) => (
              <motion.div
                key={measure.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="bg-zinc-50 rounded-xl p-5 border border-zinc-100 hover:border-emerald-200 transition-all"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-zinc-500 uppercase tracking-wide">
                    {index + 1}. {measure.name}
                  </span>
                </div>
                <p className="text-2xl font-heading font-bold text-zinc-900 mb-1">{measure.example}</p>
                <p className="text-xs text-zinc-500">{measure.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-32 bg-zinc-50" data-testid="fundmatch-features">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="mb-4 bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-0">
                How It Works
              </Badge>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-zinc-900 tracking-tight mb-6">
                AI-Powered Matching Pipeline
              </h2>
              <p className="text-lg text-zinc-600 mb-8">
                Our 3-tier probabilistic pipeline ensures you only see grants you're qualified for, 
                ranked by the factors that matter most.
              </p>

              <div className="space-y-6">
                {features.map((feature, index) => (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="flex gap-4"
                  >
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <feature.icon className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-zinc-900 mb-1">{feature.title}</h3>
                      <p className="text-sm text-zinc-600">{feature.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="bg-white rounded-2xl shadow-xl border border-zinc-100 p-6 md:p-8"
              >
                <h3 className="font-heading font-semibold text-zinc-900 mb-6">3-Tier Pipeline</h3>
                <div className="space-y-4">
                  <div className="p-4 bg-red-50 rounded-xl border border-red-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Layers className="h-4 w-4 text-red-600" />
                      <span className="font-medium text-red-900 text-sm">Tier 1: Hard Filters</span>
                    </div>
                    <p className="text-xs text-red-700">Geographic, legal status, revenue, prohibited uses</p>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
                    <div className="flex items-center gap-2 mb-2">
                      <BarChart3 className="h-4 w-4 text-amber-600" />
                      <span className="font-medium text-amber-900 text-sm">Tier 2: Soft Scoring</span>
                    </div>
                    <p className="text-xs text-amber-700">22 weighted features × Priority × Bind Strength</p>
                  </div>
                  <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="h-4 w-4 text-emerald-600" />
                      <span className="font-medium text-emerald-900 text-sm">Tier 3: Semantic Similarity</span>
                    </div>
                    <p className="text-xs text-emerald-700">Mission embedding cosine similarity</p>
                  </div>
                </div>
                <div className="mt-6 p-4 bg-zinc-900 rounded-xl">
                  <p className="text-xs text-zinc-400 mb-1">Final Score Formula</p>
                  <p className="text-sm font-mono text-white">0.6 × soft_score + 0.4 × semantic_sim</p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-400 via-slate-500 to-teal-500" />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent" />
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center relative z-10">
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-white tracking-tight mb-6">
            Start discovering grants today
          </h2>
          <p className="text-lg text-white/90 mb-10 max-w-2xl mx-auto">
            Access 600+ grant programs matched to your organization's profile, mission, and capacity.
          </p>
          <Link to="/dashboard">
            <Button
              size="lg"
              className="bg-white text-slate-700 hover:bg-zinc-100 font-medium px-8 py-6 text-base"
            >
              Get Started Free
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default FundMatchPage;
